import * as React from "react";
import { useState } from "react";
import { FormGroup } from "../Workflows/RightAside/RightAsideHelpers";
import { PopoverInvitation } from "./types";
import Input from "@/components/Input";
import { IconClose, TertiaryGrayButton, IconPlus, FormValidator } from "@velaro/velaro-component-lib";
import HtmlSkinEditor from "./HtmlSkinEditor";

interface Props {
  onUpdate(invitation: PopoverInvitation): void;
  invitation: PopoverInvitation;
  errors: Record<string, string>;
}

export default function PopoverInvitationEditor({
  invitation,
  errors,
  onUpdate
}: Props) {
  const messages = Array.isArray(invitation.messages) ? invitation.messages : [""];
  const [showHtmlEditor, setShowHtmlEditor] = useState(
    !!(invitation.htmlContent || invitation.cssContent)
  );

  return (
    <>
      <FormGroup
        label="Message delay (seconds)"
        tooltipText="The number of seconds to wait before showing the popover"
      >
        <FormValidator error={errors["messageDelaySeconds"]}>
          <Input
            type="number"
            value={invitation.messageDelaySeconds ?? 3}
            onChange={(value) =>
              onUpdate({
                ...invitation,
                messageDelaySeconds: parseInt(value) || 0
              })
            }
          />
        </FormValidator>
      </FormGroup>
      <FormGroup label="Messages">
        <FormValidator error={errors["messages"]}>
          <div className="flex flex-col gap-2">
            {messages.map((message, index) => (
              <div key={index}>
                <Input
                  value={message}
                  onChange={(value) => {
                    const newMessages = [...messages];
                    newMessages[index] = value;
                    onUpdate({ ...invitation, messages: newMessages });
                  }}
                  suffixIcon={
                    messages.length > 1 ? (
                      <div
                        className="cursor-pointer text-slate-500"
                        onClick={() => {
                          const newMessages = [...messages];
                          newMessages.splice(index, 1);
                          onUpdate({ ...invitation, messages: newMessages });
                        }}
                      >
                        <IconClose />
                      </div>
                    ) : undefined
                  }
                />
              </div>
            ))}
            <TertiaryGrayButton
              label="Add Message"
              prefixIcon={<IconPlus />}
              onClick={() =>
                onUpdate({
                  ...invitation,
                  messages: [...messages, ""]
                })
              }
            />
          </div>
        </FormValidator>
      </FormGroup>
      <FormGroup label="CTA Button Label" tooltipText="Leave empty to hide the button">
        <Input
          value={invitation.ctaLabel ?? ""}
          onChange={(value) => onUpdate({ ...invitation, ctaLabel: value })}
        />
      </FormGroup>

      {/* Custom HTML skin toggle */}
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setShowHtmlEditor((prev) => !prev)}
          className="text-xs text-blue-600 hover:underline"
        >
          {showHtmlEditor ? "▲ Hide custom HTML/CSS skin" : "▼ Custom HTML/CSS skin (advanced)"}
        </button>
        {showHtmlEditor && (
          <div className="mt-2">
            <HtmlSkinEditor
              htmlContent={invitation.htmlContent ?? ""}
              cssContent={invitation.cssContent ?? ""}
              onHtmlChange={(htmlContent) => onUpdate({ ...invitation, htmlContent })}
              onCssChange={(cssContent) => onUpdate({ ...invitation, cssContent })}
            />
          </div>
        )}
      </div>
    </>
  );
}
